import { useEffect, useRef, useState } from "react";
import {
  PULL_BLOCKERS,
  PULL_IGNORES,
  PULL_THRESHOLD,
  isInstalled,
  pullOffset,
  pullOutcome,
} from "../lib/pullRefresh.js";
import { Icon } from "./Icon.js";
import { useToast } from "./Toast.js";

/**
 * Pull down from the top of any screen to reload, in the installed app. See
 * lib/pullRefresh.ts for why only there, and what it will not throw away.
 *
 * The listeners are passive: the page still scrolls and bounces exactly as it
 * would, and this only watches how far a pull that began at the top has gone.
 */
export function PullToRefresh() {
  const [distance, setDistance] = useState(0);
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    if (!isInstalled()) return;
    let startY: number | null = null;
    let pulled = 0;

    const onStart = (event: TouchEvent) => {
      startY = null;
      if (event.touches.length !== 1 || window.scrollY > 0) return;
      if (document.querySelector(PULL_BLOCKERS)) return;
      if (event.target instanceof Element && event.target.closest(PULL_IGNORES)) return;
      startY = event.touches[0]!.clientY;
      pulled = 0;
    };

    const onMove = (event: TouchEvent) => {
      if (startY === null) return;
      const touch = event.touches[0];
      if (!touch) return;
      pulled = Math.max(0, touch.clientY - startY);
      setDistance(pulled);
    };

    const onEnd = () => {
      if (startY === null) return;
      startY = null;
      setDistance(0);
      const outcome = pullOutcome(pulled, document.querySelector("[data-unsaved]") !== null);
      pulled = 0;
      if (outcome === "refresh") window.location.reload();
      else if (outcome === "blocked")
        toastRef.current("Save or discard your changes first, then pull to refresh.");
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  if (distance <= 0) return null;
  const ready = distance >= PULL_THRESHOLD;
  return (
    <div
      className={ready ? "pull-refresh pull-refresh-ready" : "pull-refresh"}
      style={{
        translate: `-50% ${pullOffset(distance)}px`,
        rotate: `${Math.min(distance, PULL_THRESHOLD) * 3}deg`,
      }}
      aria-hidden="true"
    >
      <Icon name="refresh" size={18} />
    </div>
  );
}
